import '@mantine/core/styles.css'
import '@mantine/core/styles.layer.css'
import '@mantine/dates/styles.css'
import '@mantine/notifications/styles.css'
import 'mantine-contextmenu/styles.layer.css'
import 'mantine-react-table/styles.css'
import './assets/layout.css'

import { createTheme, MantineProvider } from '@mantine/core'
import { DatesProvider } from '@mantine/dates'
import { Notifications } from '@mantine/notifications'
import { GoogleOAuthProvider } from '@react-oauth/google'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { APIProvider } from '@vis.gl/react-google-maps'
import { ContextMenuProvider } from 'mantine-contextmenu'
import { useEffect, useRef } from 'react'
import { RouterProvider } from 'react-router-dom'
import { Manager } from 'socket.io-client'

import { AuthProvider } from './auth/auth-context'
import { ErrorBoundary } from './components/ErrorBoundary'
import { SocketContext } from './context/socket-context'
import { Router } from './router/router'
import { API_HOST, GOOGLE_MAPS_API_KEY, VITE_ENVIRONMENT, VITE_GOOGLE_CLIENT_ID } from './utils/constants'
import { createQueryErrorHandler } from './utils/errorHandler'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      onError: createQueryErrorHandler('mutation'),
    },
  },
})

Sentry.init({
  dsn: 'https://665876663c08f6df6e6836e5718970d2@o4507527044661248.ingest.us.sentry.io/4507946714464256',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    // Explicitly enable global handlers to catch uncaught errors and unhandled promise rejections
    Sentry.globalHandlersIntegration({
      onerror: true,
      onunhandledrejection: true,
    }),
  ],
  enabled: VITE_ENVIRONMENT === 'prod',
  // Tracing
  tracesSampleRate: 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ['app2.einsteinmoving.com', /^https:\/\/api2\.einsteinmoving\.com/],
  // Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
})

// Backup global error handlers to ensure critical errors are always captured
// These fire even if Sentry's built-in handlers miss something
if (VITE_ENVIRONMENT === 'prod') {
  window.addEventListener('error', (event) => {
    if (event.error) {
      Sentry.captureException(event.error, {
        extra: {
          source: 'window.onerror',
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      })
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))
    Sentry.captureException(error, {
      extra: {
        source: 'unhandledrejection',
        reason: String(event.reason),
      },
    })
  })
}

const inputStyles = {
  label: { color: '#6C757D', fontWeight: 500, fontSize: '13px' },
  input: { backgroundColor: '#fafbfc', borderColor: '#E9ECEF' },
}

const theme = createTheme({
  fontFamily: 'Roboto',
  // primaryColor: 'orange',
  components: {
    TextInput: {
      styles: inputStyles,
    },
    Select: {
      styles: inputStyles,
    },
    Textarea: {
      styles: inputStyles,
    },
    NumberInput: {
      styles: inputStyles,
    },
    InputBase: {
      styles: inputStyles,
    },
    DatePickerInput: {
      styles: inputStyles,
    },
    DateInput: {
      styles: inputStyles,
    },
  },
})

export const App = () => {
  const router = Router()

  // Force the WebSocket transport (skip HTTP long-polling). Polling makes
  // multiple separate requests that must hit the same API instance, which would
  // require sticky sessions once the API runs on more than one instance.
  const { current: manager } = useRef<Manager>(new Manager(API_HOST, { transports: ['websocket'] }))

  useEffect(() => {
    const titleTag = document.querySelector('title')
    if (titleTag && VITE_ENVIRONMENT !== 'prod') {
      titleTag.innerText = 'EMMA V4 - DEVELOPMENT'
    }
  }, [])

  return (
    <ErrorBoundary>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <QueryClientProvider client={queryClient}>
          <MantineProvider theme={theme}>
            <DatesProvider settings={{ firstDayOfWeek: 0 }}>
              <ContextMenuProvider>
                <Notifications />
                <GoogleOAuthProvider clientId={VITE_GOOGLE_CLIENT_ID ?? ''}>
                  <AuthProvider>
                    <SocketContext.Provider value={{ socketManager: manager }}>
                      <RouterProvider router={router} />
                    </SocketContext.Provider>
                  </AuthProvider>
                </GoogleOAuthProvider>
              </ContextMenuProvider>
            </DatesProvider>
          </MantineProvider>
          {VITE_ENVIRONMENT !== 'prod' && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </APIProvider>
    </ErrorBoundary>
  )
}
